import { useState, useMemo } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Link from '@cloudscape-design/components/link';
import {
  extractSafeLinksInfo,
  getExampleSafeLinksUrl,
  decodeBatchSafeLinks,
} from '@utils/safeLinkDecoder';
import { useClipboard } from '@hooks/useClipboard';

export const SafeLinksTab = () => {
  const [inputText, setInputText] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  
  // Use clipboard hook for cross-browser support
  const { pasteFromClipboard, copyToClipboard, error: clipboardError, clearError } = useClipboard();

  // Process input - support single URL or batch processing
  const processedUrls = useMemo(() => {
    if (!inputText.trim()) {
      return [];
    }

    // Split by newlines to support batch processing
    const urls = inputText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return decodeBatchSafeLinks(urls);
  }, [inputText]);

  // Get detailed info for the first URL if only one is provided
  const detailedInfo = useMemo(() => {
    if (processedUrls.length === 1 && processedUrls[0].output) {
      return extractSafeLinksInfo(processedUrls[0].input);
    }
    return null;
  }, [processedUrls]);

  // Statistics
  const statistics = useMemo(() => {
    const total = processedUrls.length;
    const successful = processedUrls.filter((u) => u.output !== null).length;
    const failed = total - successful;

    return { total, successful, failed };
  }, [processedUrls]);

  const handlePasteFromClipboard = async () => {
    const text = await pasteFromClipboard();
    if (text) {
      setInputText(text);
    }
  };

  const handleTryExample = () => {
    setInputText(getExampleSafeLinksUrl());
  };

  const handleClear = () => {
    setInputText('');
  };

  const handleCopyDecoded = async (url: string) => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopySuccess('Decoded URL copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const handleCopyAllDecoded = async () => {
    const decodedUrls = processedUrls
      .filter((u) => u.output !== null)
      .map((u) => u.output)
      .join('\n');

    const success = await copyToClipboard(decodedUrls);
    if (success) {
      setCopySuccess(`Copied ${statistics.successful} decoded URL(s) to clipboard!`);
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  return (
    <SpaceBetween size="l">
      {copySuccess && (
        <Alert key="success-alert" type="success" dismissible onDismiss={() => setCopySuccess('')}>
          {copySuccess}
        </Alert>
      )}

      {clipboardError && (
        <Alert key="clipboard-error-alert" type="warning" dismissible onDismiss={clearError}>
          {clipboardError}
        </Alert>
      )}

      <ColumnLayout key="main-layout" columns={inputText ? 2 : 1} variant="text-grid" minColumnWidth={300}>
        <div>
          <SpaceBetween size="l">
            <Container
              header={
                <Header
                  variant="h2"
                  description="Paste one or more Microsoft 365 Safe Links URLs to decode"
                >
                  Safe Links Input
                </Header>
              }
            >
              <SpaceBetween size="m">
                <Textarea
                  value={inputText}
                  onChange={({ detail }) => setInputText(detail.value)}
                  placeholder="Paste Safe Links URL(s) here... (one per line for batch processing)"
                  rows={8}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handlePasteFromClipboard}>Paste from Clipboard</Button>
                  <Button onClick={handleTryExample}>Try Example</Button>
                  <Button onClick={handleClear}>Clear</Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>

            {inputText && processedUrls.length > 0 && (
              <Container
                header={
                  <Header
                    variant="h2"
                    description={`${statistics.successful} of ${statistics.total} URL(s) decoded successfully`}
                  >
                    Decoded Results
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {processedUrls.map((result, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '12px',
                        backgroundColor: result.output ? '#f0f8ff' : '#fff3cd',
                        borderRadius: '8px',
                        border: `1px solid ${result.output ? '#0972d3' : '#ffc107'}`,
                      }}
                    >
                      <SpaceBetween size="s">
                        {processedUrls.length > 1 && (
                          <Box variant="h4">
                            URL {index + 1}{' '}
                            <Badge color={result.output ? 'green' : 'red'}>
                              {result.output ? 'Success' : 'Failed'}
                            </Badge>
                          </Box>
                        )}

                        {result.output ? (
                          <>
                            <div>
                              <Box variant="awsui-key-label">Decoded URL:</Box>
                              <Link href={result.output} external target="_blank">
                                <div
                                  style={{
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    wordBreak: 'break-all',
                                    marginTop: '4px',
                                  }}
                                >
                                  {result.output}
                                </div>
                              </Link>
                            </div>
                            <Button
                              iconName="copy"
                              onClick={() => handleCopyDecoded(result.output!)}
                            >
                              Copy Decoded URL
                            </Button>
                          </>
                        ) : (
                          <Alert type="error" header="Decoding Failed">
                            {result.error || 'Unknown error occurred'}
                          </Alert>
                        )}
                      </SpaceBetween>
                    </div>
                  ))}

                  {statistics.successful > 1 && (
                    <Button iconName="copy" onClick={handleCopyAllDecoded} variant="primary">
                      Copy All {statistics.successful} Decoded URLs
                    </Button>
                  )}
                </SpaceBetween>
              </Container>
            )}
          </SpaceBetween>
        </div>

        {inputText && detailedInfo && (
          <div>
            <Container
              header={
                <Header variant="h2" description="Detailed information about the Safe Links URL">
                  URL Analysis
                </Header>
              }
            >
              <SpaceBetween size="l">
                {/* Validation Status */}
                <div>
                  <Box variant="awsui-key-label">Validation Status</Box>
                  <div style={{ marginTop: '4px' }}>
                    <Badge color={detailedInfo.isValid ? 'green' : 'red'}>
                      {detailedInfo.isValid ? 'Valid Safe Links URL' : 'Invalid URL'}
                    </Badge>
                  </div>
                </div>

                {detailedInfo.error && (
                  <Alert type="error" header="Error">
                    {detailedInfo.error}
                  </Alert>
                )}

                {/* URL Details */}
                {detailedInfo.isValid && (
                  <>
                    <ColumnLayout columns={2} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">Region</Box>
                        <Box variant="p">{detailedInfo.region || 'Unknown'}</Box>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Domain</Box>
                        <Box variant="p">
                          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {detailedInfo.domain}
                          </span>
                        </Box>
                      </div>
                    </ColumnLayout>

                    {/* Original URL */}
                    <div>
                      <Box variant="awsui-key-label">Original URL</Box>
                      {detailedInfo.originalUrl && (
                        <Link href={detailedInfo.originalUrl} external target="_blank">
                          <div
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              wordBreak: 'break-all',
                              marginTop: '4px',
                            }}
                          >
                            {detailedInfo.originalUrl}
                          </div>
                        </Link>
                      )}
                    </div>

                    {/* Metadata */}
                    {(detailedInfo.data || detailedInfo.sdata || detailedInfo.reserved) && (
                      <ExpandableSection headerText="Safe Links Metadata" variant="container">
                        <SpaceBetween size="m">
                          {detailedInfo.data && (
                            <div>
                              <Box variant="awsui-key-label">Data Parameter</Box>
                              <div
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  wordBreak: 'break-all',
                                  padding: '8px',
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: '4px',
                                  marginTop: '4px',
                                }}
                              >
                                {detailedInfo.data}
                              </div>
                            </div>
                          )}

                          {detailedInfo.sdata && (
                            <div>
                              <Box variant="awsui-key-label">Signature Data (sdata)</Box>
                              <div
                                style={{
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  wordBreak: 'break-all',
                                  padding: '8px',
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: '4px',
                                  marginTop: '4px',
                                }}
                              >
                                {detailedInfo.sdata}
                              </div>
                            </div>
                          )}

                          {detailedInfo.reserved !== null && (
                            <div>
                              <Box variant="awsui-key-label">Reserved Flag</Box>
                              <Box variant="p">{detailedInfo.reserved}</Box>
                            </div>
                          )}
                        </SpaceBetween>
                      </ExpandableSection>
                    )}
                  </>
                )}
              </SpaceBetween>
            </Container>
          </div>
        )}

        {inputText && statistics.total > 1 && (
          <div>
            <Container
              header={
                <Header variant="h2" description="Summary of batch processing">
                  Processing Statistics
                </Header>
              }
            >
              <ColumnLayout columns={3} variant="text-grid">
                <div>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Total URLs</Box>
                    <Box variant="h1" fontSize="display-l">
                      {statistics.total}
                    </Box>
                  </SpaceBetween>
                </div>
                <div>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Successful</Box>
                    <Box variant="h1" fontSize="display-l" color="text-status-success">
                      {statistics.successful}
                    </Box>
                  </SpaceBetween>
                </div>
                <div>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Failed</Box>
                    <Box variant="h1" fontSize="display-l" color="text-status-error">
                      {statistics.failed}
                    </Box>
                  </SpaceBetween>
                </div>
              </ColumnLayout>
            </Container>
          </div>
        )}
      </ColumnLayout>

    </SpaceBetween>
  );
};

